import { useState } from "react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { Feature, IPaginator } from "common/types";
import { Optional } from "common/utils/types";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";

import PillButton from "client/common/components/buttons/PillButton";
import { useFeatures } from "client/clinics/hooks";

import { PRODUCT_FILTER_DEFINITIONS } from "../constants/filterDefinitions";
import { ProductSearchFilters, SelectedProductFilters } from "../types";
import { getSearchString } from "../utils";
import ProductSearchFilterModal from "./ProductSearchFilterModal";
import AppliedFilterTags from "./AppliedFilterTags";
import ProductFilterPanelModal from "./ProductFilterPanelModal";

import styles from "../styles/ProductCardListHeader.module.less";

type ProductCardListHeaderProps = {
  paginator: IPaginator;
  isRefetching: boolean;
  /** Pluralized in the count, e.g. "deal" reads "Showing 1 - 14 of 14 deals". */
  entityName: string;
  filterOptions: Optional<ProductSearchFilters>;
  filters: SelectedProductFilters;
  onChange: (search: Partial<SelectedProductFilters>) => void;
  /** Whether the page offers filtering; false renders the result count alone. */
  showFilters?: boolean;
  /**
   * Whether `filterOptions` is the complete set, with counts that track the narrowing. Only
   * then does the modal get counts, collapsing and the panel's option list.
   */
  hasCompleteOptions?: boolean;
};

type FilterActionsProps = Pick<
  ProductCardListHeaderProps,
  "filterOptions" | "filters" | "onChange"
> & {
  hasCompleteOptions: boolean;
};

const FilterActions = ({
  filterOptions,
  filters,
  onChange,
  hasCompleteOptions,
}: FilterActionsProps) => {
  const [isFiltering, setIsFiltering] = useState(false);
  const isFiltersV2 = useFeatures()[Feature.StorefrontFiltersV2];

  return (
    <div className={styles.actions}>
      {filterOptions && (
        <AppliedFilterTags<SelectedProductFilters, ProductSearchFilters>
          definitions={PRODUCT_FILTER_DEFINITIONS}
          options={filterOptions}
          value={filters}
          onChange={onChange}
          variant={isFiltersV2 ? "accent" : "primary"}
        />
      )}
      <PillButton
        variant="secondary"
        size="s"
        type="button"
        onClick={() => setIsFiltering(true)}
        disabled={!filterOptions}
      >
        <span className={styles.filter}>
          <Icon icon={faFilter} /> Filter
        </span>
      </PillButton>
      {filterOptions &&
        (hasCompleteOptions ? (
          <ProductFilterPanelModal
            options={filterOptions}
            isVisible={isFiltering}
            onClose={() => setIsFiltering(false)}
            value={filters}
            onChange={onChange}
          />
        ) : (
          <ProductSearchFilterModal
            options={filterOptions}
            isCollapsible={false}
            isVisible={isFiltering}
            onClose={() => setIsFiltering(false)}
            value={filters}
            onChange={onChange}
          />
        ))}
    </div>
  );
};

/** The bar above a ProductCardList: result count, active filter chips, and the filter modal. */
const ProductCardListHeader = ({
  paginator,
  isRefetching,
  entityName,
  filterOptions,
  filters,
  onChange,
  showFilters = true,
  hasCompleteOptions = false,
}: ProductCardListHeaderProps) => (
  <div className={styles.header}>
    <div className={styles.label}>
      {isRefetching ? <Spinner /> : getSearchString(paginator, entityName)}
    </div>
    {showFilters && (
      <FilterActions
        filterOptions={filterOptions}
        filters={filters}
        onChange={onChange}
        hasCompleteOptions={hasCompleteOptions}
      />
    )}
  </div>
);

export default ProductCardListHeader;
